import { useState, useRef } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CloudArrowUp, Download, FileXls, CheckCircle, XCircle, Warning } from "@phosphor-icons/react";

// Configurable — pass `mode="items"` or `mode="vendors"`
const CONFIG = {
  items: {
    title: "Bulk Upload Items",
    endpoint: "/inventory/items/bulk-import",
    payloadKey: "items",
    headers: ["Code", "Name", "Category", "Unit", "MOQ", "ROL", "OpeningStock", "UnitPrice", "HSN_SAC", "GST_Rate", "Description"],
    sample: [
      ["HK-TISSUE-01", "Tissue Roll", "Housekeeping", "BOX", 10, 5, 20, 120, "4823", 18, "2-ply premium"],
      ["IT-USB-32", "USB 32GB", "IT", "PCS", 20, 10, 100, 350, "8523", 18, "Kingston / SanDisk"],
    ],
    fileName: "inventory-items-template.xlsx",
    normalize: (row) => {
      const g = (...keys) => {
        for (const k of keys) if (row[k] !== undefined && row[k] !== null && String(row[k]).trim() !== "") return row[k];
        return "";
      };
      return {
        code: String(g("Code", "code")).trim(),
        name: String(g("Name", "name")).trim(),
        category: String(g("Category", "category")).trim(),
        unit: String(g("Unit", "unit", "UOM") || "pcs").trim(),
        moq: Number(g("MOQ", "moq")) || 0,
        rol: Number(g("ROL", "rol")) || 0,
        opening_stock: Number(g("OpeningStock", "opening_stock", "Stock")) || 0,
        unit_price: Number(g("UnitPrice", "unit_price", "Price")) || 0,
        hsn_sac: String(g("HSN_SAC", "hsn_sac", "HSN")).trim(),
        gst_rate: Number(g("GST_Rate", "gst_rate", "GST")) || 0,
        description: String(g("Description", "description")).trim(),
      };
    },
    label: "code",
  },
  vendors: {
    title: "Bulk Upload Vendors",
    endpoint: "/inventory/vendors/bulk-import",
    payloadKey: "vendors",
    headers: ["Name", "ContactPerson", "Email", "Phone", "Address", "GSTIN", "PAN", "PaymentTerms", "Categories"],
    sample: [
      ["ABC Traders", "John Doe", "john@abc.com", "9876543210", "Mumbai", "27ABCDE1234F1Z5", "ABCDE1234F", "Net 30", "Housekeeping, Admin"],
      ["Tech Depot", "Jane Rao", "jane@tech.co", "9123456789", "Bangalore", "", "", "Advance", "IT"],
    ],
    fileName: "inventory-vendors-template.xlsx",
    normalize: (row) => {
      const g = (...keys) => { for (const k of keys) if (row[k] !== undefined && row[k] !== null && String(row[k]).trim() !== "") return row[k]; return ""; };
      return {
        name: String(g("Name", "name")).trim(),
        contact_person: String(g("ContactPerson", "contact_person", "Contact")).trim(),
        email: String(g("Email", "email")).trim(),
        phone: String(g("Phone", "phone")).trim(),
        address: String(g("Address", "address")).trim(),
        gstin: String(g("GSTIN", "gstin")).trim(),
        pan: String(g("PAN", "pan")).trim(),
        payment_terms: String(g("PaymentTerms", "payment_terms", "Terms")).trim(),
        categories: String(g("Categories", "categories")).trim(),
      };
    },
    label: "name",
  },
};

export default function BulkImportDialog({ open, onOpenChange, mode = "items", onImported }) {
  const cfg = CONFIG[mode];
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setRows([]); setFileName(""); setResult(null); if (fileRef.current) fileRef.current.value = ""; };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([cfg.headers, ...cfg.sample]);
    ws["!cols"] = cfg.headers.map(() => ({ wch: 22 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, cfg.fileName);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      const normalized = json.map(cfg.normalize).filter((r) => r[cfg.label]);
      setRows(normalized);
      normalized.length ? toast.success(`${normalized.length} rows ready`) : toast.error("No rows detected — check template columns.");
    } catch {
      toast.error("Failed to parse file. Use .xlsx or .csv from the template.");
    }
  };

  const handleImport = async () => {
    if (!rows.length) return toast.error("Please upload a file first");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}${cfg.endpoint}`, { [cfg.payloadKey]: rows }, { withCredentials: true });
      setResult(res.data);
      toast.success(`Imported ${res.data.created_count}, skipped ${res.data.skipped_count}, errors ${res.data.error_count}`);
      onImported?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk import failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => { reset(); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-3xl" data-testid={`bulk-${mode}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileXls size={22} weight="duotone" className="text-emerald-600" /> {cfg.title}
          </DialogTitle>
          <DialogDescription>
            Upload an Excel (.xlsx) or CSV file. Duplicate {cfg.label}s are skipped, invalid rows are reported.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-md bg-secondary/40 border border-border">
              <div className="text-sm">
                <p className="font-medium">Download the template</p>
                <p className="text-muted-foreground text-xs">Columns: {cfg.headers.join(", ")}</p>
              </div>
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid={`bulk-${mode}-template-btn`}>
                <Download size={14} className="mr-2" /> Download template
              </Button>
            </div>

            <div>
              <Label htmlFor="bulk-file">Excel / CSV file</Label>
              <Input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} data-testid={`bulk-${mode}-file-input`} />
              {fileName && <p className="mt-1 text-xs text-muted-foreground">Loaded: <strong>{fileName}</strong> ({rows.length} rows)</p>}
            </div>

            {rows.length > 0 && (
              <div className="border border-border rounded-md">
                <ScrollArea className="h-52">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Row</TableHead>
                      {cfg.headers.slice(0, 4).map((h) => <TableHead key={h}>{h}</TableHead>)}
                    </TableRow></TableHeader>
                    <TableBody>
                      {rows.slice(0, 200).map((r, i) => {
                        const keys = Object.keys(r);
                        return (
                          <TableRow key={i}>
                            <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                            {keys.slice(0, 4).map((k) => <TableCell key={k}>{String(r[k] || "")}</TableCell>)}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </ScrollArea>
                {rows.length > 200 && <div className="px-3 py-2 text-xs text-muted-foreground border-t">Showing first 200 of {rows.length}. All rows will import.</div>}
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-center">
                <CheckCircle size={20} weight="fill" className="text-emerald-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-emerald-700">{result.created_count}</p>
                <p className="text-xs">Created</p>
              </div>
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-center">
                <Warning size={20} weight="fill" className="text-amber-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-amber-700">{result.skipped_count}</p>
                <p className="text-xs">Skipped (exists)</p>
              </div>
              <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3 text-center">
                <XCircle size={20} weight="fill" className="text-destructive mx-auto mb-1" />
                <p className="text-2xl font-bold text-destructive">{result.error_count}</p>
                <p className="text-xs">Errors</p>
              </div>
            </div>
            {(result.errors?.length > 0 || result.skipped?.length > 0) && (
              <ScrollArea className="h-40 border border-border rounded-md">
                <Table>
                  <TableHeader><TableRow><TableHead>Row</TableHead><TableHead>{cfg.label}</TableHead><TableHead>Reason</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {result.errors?.map((e, i) => (
                      <TableRow key={`e${i}`}><TableCell>{e.row}</TableCell><TableCell>{e[cfg.label]}</TableCell><TableCell className="text-destructive">{e.reason}</TableCell></TableRow>
                    ))}
                    {result.skipped?.map((s, i) => (
                      <TableRow key={`s${i}`}><TableCell>{s.row}</TableCell><TableCell>{s[cfg.label]}</TableCell><TableCell className="text-amber-700">{s.reason}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose}>{result ? "Done" : "Cancel"}</Button>
          {!result && (
            <Button onClick={handleImport} disabled={submitting || rows.length === 0} data-testid={`bulk-${mode}-import-btn`}>
              <CloudArrowUp size={16} className="mr-2" />
              {submitting ? "Importing..." : `Import ${rows.length || ""}`}
            </Button>
          )}
          {result && (
            <Button onClick={reset} data-testid={`bulk-${mode}-again-btn`}>Upload another file</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
